import {
  faQuestionCircle,
  IconDefinition,
} from '@fortawesome/free-regular-svg-icons';
import {
  faBusAlt,
  faBusSimple,
  faCar,
  faCarAlt,
  faFerry,
  faPlane,
  faTaxi,
  faTrain,
} from '@fortawesome/free-solid-svg-icons';
import {
  AssignmentModel,
  AssignmentTypeEnum,
  AssignmentViewModel,
  UserModel,
} from 'api/model';
import { IAddress } from 'utils/address';
import { makeDateComparator } from 'utils/sorting';

const getPlaceholderIcon = (type: AssignmentTypeEnum): IconDefinition => {
  switch (type) {
    case AssignmentTypeEnum.CarPlaceholder:
      return faQuestionCircle;
    case AssignmentTypeEnum.GoByBoat:
      return faFerry;
    case AssignmentTypeEnum.GoByBus:
      return faBusAlt;
    case AssignmentTypeEnum.GoByPlane:
      return faPlane;
    case AssignmentTypeEnum.GoByTrain:
      return faTrain;
    case AssignmentTypeEnum.GoByTaxi:
      return faTaxi;
    case AssignmentTypeEnum.GoByPublicTransport:
      return faBusSimple;
    default:
      return faCarAlt;
  }
};

export const getAssignmentTypeIcon = (
  type: AssignmentTypeEnum
): IconDefinition => {
  switch (type) {
    case AssignmentTypeEnum.GoByBus:
      return faBusAlt;
    case AssignmentTypeEnum.GoByPlane:
      return faPlane;
    case AssignmentTypeEnum.GoByTrain:
      return faTrain;
    case AssignmentTypeEnum.GoByTaxi:
      return faTaxi;
    case AssignmentTypeEnum.GoByBoat:
      return faFerry;
    case AssignmentTypeEnum.GoByPublicTransport:
      return faBusSimple;
    case AssignmentTypeEnum.CarPlaceholder:
      return faQuestionCircle;
    default:
      return faCar;
  }
};

type AssignmentDetails = {
  placeholderStartTime: Date;
  placeHolderToAddress: IAddress;
  assignedToUser?: UserModel;
  estimatedStartOfLastAssignment?: Date;
  assignment: AssignmentViewModel;
};

export const getLastAssignmentDetails = (
  bookedAssignmentsArray: AssignmentViewModel[]
): AssignmentDetails | undefined => {
  if (bookedAssignmentsArray.length === 0) {
    return undefined; // Explicitly return undefined
  }

  const bookedAssignments = bookedAssignmentsArray.sort(
    makeDateComparator((a) => a.assignment.estimatedStartTime ?? new Date())
  );

  const lastCaseOfDay = bookedAssignments[bookedAssignments.length - 1];

  if (
    lastCaseOfDay.assignment.estimatedDuration === undefined ||
    lastCaseOfDay.assignment.estimatedDuration === null
  ) {
    return undefined; // Explicitly return undefined
  }

  const estimatedStartOfLastAssignment =
    lastCaseOfDay.assignment.estimatedStartTime ??
    lastCaseOfDay.assignment.startTime;

  if (!estimatedStartOfLastAssignment) {
    return undefined; // Explicitly return undefined
  }

  const estimatedDurationOfLastAssignment =
    lastCaseOfDay.assignment.estimatedDuration;

  const placeholderStartTime = new Date(
    estimatedStartOfLastAssignment.getTime() + estimatedDurationOfLastAssignment
  );

  // Set from address details to the last assignment's to address details
  const placeHolderToAddress: IAddress = {
    name: lastCaseOfDay.assignment.toName,
    address: lastCaseOfDay.assignment.toAddress,
    city: lastCaseOfDay.assignment.toCity,
    zip: lastCaseOfDay.assignment.toZip,
    county: lastCaseOfDay.assignment.toCounty,
  };

  const assignedToUser = lastCaseOfDay.assignment.assignedTo;

  const result: AssignmentDetails = {
    placeholderStartTime,
    placeHolderToAddress,
    estimatedStartOfLastAssignment,
    assignedToUser,
    assignment: lastCaseOfDay,
  };

  return result;
};

export const getWorkTimeIncludingNewAssignment = (
  assignment: AssignmentModel,
  workHoursToday: number
) => {
  // get the current estimated duration in hours (default to 0)
  if (!assignment.estimatedDuration) {
    return undefined;
  }
  const estimatedDuration = assignment.estimatedDuration!;
  const alreadyUsedTime = workHoursToday * 60 * 60 * 1000;

  return estimatedDuration + alreadyUsedTime;
};

export const getTimeLeftIncludingNewAssignment = (
  assignment: AssignmentModel,
  consecutiveHoursOfRestLast24Hours: number,
  workHoursToday: number
) => {
  // get the current estimated duration in hours (default to 0)
  if (!assignment.estimatedDuration) {
    return undefined;
  }
  const estimatedDuration = assignment.estimatedDuration!;
  const totalTimeAllowedToday =
    (24 - consecutiveHoursOfRestLast24Hours) * 60 * 60 * 1000;
  const alreadyUsedTime = workHoursToday * 60 * 60 * 1000;

  const timeLeftOfDay =
    totalTimeAllowedToday - estimatedDuration - alreadyUsedTime;

  return timeLeftOfDay;
};

export default getPlaceholderIcon;
